import React from "react";
import { Grid, makeStyles, Typography } from "@material-ui/core";
import { getAlertColor, getAlertOpacity, isSMAUser } from "../helpers/dashboardHelpers";
import AlertWidget from "../helpers/AlertWidget";

interface KPIWidgetPTProps {
  totalTransfers: number;
  avgTransferPerDay: number;
  transferPerHour: number;
  transferDistance: number;
  percentMultiLoad: number;
  userRoles: any;
  transferPerDayRedAlert: number | null;
  transferPerDayYellowAlert: number | null;
  transferPerHourRedAlert: number | null;
  transferPerHourYellowAlert: number | null;
  transferDistanceRedAlert: number | null;
  transferDistanceYellowAlert: number | null;
  percentMultiLoadRedAlert: number | null;
  percentMultiLoadYellowAlert: number | null;
}

const borderCellValue = "1px solid rgba(255, 255, 255, 0.17)";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    width: "100%",
    flexWrap: "wrap",
  },
  cell: {
    flex: 1,
    border: borderCellValue,
    borderTop: 0,
    padding: "0 5px",

    "&:first-child": {
      borderLeft: 0,
    },
    "&:last-child": {
      borderRight: 0,
    },

    "@media (max-width: 1280px) and (min-width: 961px)": {
      flex: "1 1 33%",

      "&:nth-child(3)": {
        borderRight: 0,
      },
      "&:nth-child(4)": {
        borderLeft: 0,
      },
    },

    "@media (max-width: 960px)": {
      flex: "1 1 50%",

      "&:nth-child(even)": {
        borderLeft: 0,
      },
      "&:nth-child(2n+1)": {
        borderLeft: 0,
      },
    },
  },
  leftCell: {
    border: borderCellValue,
    borderTop: 0,
    borderLeft: 0,
  },
  rightCell: {
    border: borderCellValue,
    borderTop: 0,
    borderLeft: 0,
  },
  labelContainer: {
    textAlign: "center",
    paddingTop: 60,
    paddingBottom: 50,
  },
}));

const KPIWidgetPT: React.FC<KPIWidgetPTProps> = ({
  totalTransfers,
  avgTransferPerDay,
  transferPerHour,
  transferDistance,
  percentMultiLoad,
  userRoles,
  transferPerDayRedAlert,
  transferPerDayYellowAlert,
  transferPerHourRedAlert,
  transferPerHourYellowAlert,
  transferDistanceRedAlert,
  transferDistanceYellowAlert,
  percentMultiLoadRedAlert,
  percentMultiLoadYellowAlert,
}) => {
  const classes = useStyles();
  return (
    <div className={classes.root}>
      <div className={classes.cell}>
        <div className={classes.labelContainer}>
          <Typography variant="h2">{totalTransfers}</Typography>
          <Typography variant="h3" style={{ opacity: "50%" }}>
            Total Transfers
          </Typography>
        </div>
      </div>
      <div className={classes.cell}>
        <div className={classes.labelContainer}>
          <Typography
            variant="h2"
            style={{
              color: getAlertColor(userRoles, avgTransferPerDay, transferPerDayRedAlert, transferPerDayYellowAlert),
            }}
          >
            {avgTransferPerDay}
          </Typography>
          <Typography
            variant="h3"
            style={{
              opacity: getAlertOpacity(userRoles, avgTransferPerDay, transferPerDayRedAlert, transferPerDayYellowAlert),
              maxWidth: "3",
              color: getAlertColor(userRoles, avgTransferPerDay, transferPerDayRedAlert, transferPerDayYellowAlert),
            }}
          >
            Transfers Per Day
          </Typography>
          {isSMAUser(userRoles) && <AlertWidget userRoles={userRoles} currentValue={avgTransferPerDay} redAlert={transferPerDayRedAlert} yellowAlert={transferPerDayYellowAlert} isItAbove={false} />}
        </div>
      </div>

      <div className={classes.cell}>
        <div className={classes.labelContainer}>
          <Typography
            variant="h2"
            style={{
              color: getAlertColor(userRoles, transferPerHour, transferPerHourRedAlert, transferPerHourYellowAlert),
            }}
          >
            {transferPerHour}
          </Typography>
          <Typography
            variant="h3"
            style={{
              opacity: getAlertOpacity(userRoles, transferPerHour, transferPerHourRedAlert, transferPerHourYellowAlert),
              maxWidth: "3",
              color: getAlertColor(userRoles, transferPerHour, transferPerHourRedAlert, transferPerHourYellowAlert),
            }}
          >
            Transfers Per Hour
          </Typography>
          <Typography
            style={{
              opacity: getAlertOpacity(userRoles, transferPerHour, transferPerHourRedAlert, transferPerHourYellowAlert),
              fontSize: "13px",
              marginTop: 5,
              color: getAlertColor(userRoles, transferPerHour, transferPerHourRedAlert, transferPerHourYellowAlert),
            }}
          >
            Based on a standard 40 hour week
          </Typography>          
          {isSMAUser(userRoles) && <AlertWidget userRoles={userRoles} currentValue={transferPerHour} redAlert={transferPerHourRedAlert} yellowAlert={transferPerHourYellowAlert} isItAbove={false} />}
        </div>
      </div>
      <div className={classes.cell}>
        <div className={classes.labelContainer}>
          <Typography
            variant="h2"
            style={{
              display: "inline-block",
              color: getAlertColor(userRoles, transferDistance, transferDistanceRedAlert, transferDistanceYellowAlert, true),
            }}
          >
            {transferDistance}
          </Typography>
          <Typography
            variant="h3"
            style={{
              opacity: getAlertOpacity(userRoles, transferDistance, transferDistanceRedAlert, transferDistanceYellowAlert, true),
              maxWidth: "3",
              display: "inline-block",
              color: getAlertColor(userRoles, transferDistance, transferDistanceRedAlert, transferDistanceYellowAlert, true),
            }}
          >
            km
          </Typography>
          <Typography
            variant="h3"
            style={{
              opacity: getAlertOpacity(userRoles, transferDistance, transferDistanceRedAlert, transferDistanceYellowAlert, true),
              maxWidth: "3",
              color: getAlertColor(userRoles, transferDistance, transferDistanceRedAlert, transferDistanceYellowAlert, true),
            }}
          >
            Mean Transfer Distance
          </Typography>
          {isSMAUser(userRoles) && <AlertWidget userRoles={userRoles} currentValue={transferDistance} redAlert={transferDistanceRedAlert} yellowAlert={transferDistanceYellowAlert} isItAbove={true} />}
        </div>
      </div>
      <div className={classes.cell}>
        <div className={classes.labelContainer}>
          <Typography
            variant="h2"
            style={{
              display: "inline-block",
              color: getAlertColor(userRoles, percentMultiLoad, percentMultiLoadRedAlert, percentMultiLoadYellowAlert),
            }}
          >
            {percentMultiLoad}
          </Typography>
          <Typography
            variant="h3"
            style={{
              opacity: getAlertOpacity(userRoles, percentMultiLoad, percentMultiLoadRedAlert, percentMultiLoadYellowAlert),
              maxWidth: "3",
              display: "inline-block",
              color: getAlertColor(userRoles, percentMultiLoad, percentMultiLoadRedAlert, percentMultiLoadYellowAlert),
            }}
          >
            %
          </Typography>
          <Typography
            variant="h3"
            style={{
              opacity: getAlertOpacity(userRoles, percentMultiLoad, percentMultiLoadRedAlert, percentMultiLoadYellowAlert),
              maxWidth: "3",
              color: getAlertColor(userRoles, percentMultiLoad, percentMultiLoadRedAlert, percentMultiLoadYellowAlert),
            }}
          >
            Multiload Trip
          </Typography>
          {isSMAUser(userRoles) && <AlertWidget userRoles={userRoles} currentValue={percentMultiLoad} redAlert={percentMultiLoadRedAlert} yellowAlert={percentMultiLoadYellowAlert} isItAbove={false} />}
        </div>
      </div>
    </div>
  );
};

export default KPIWidgetPT;
